import React, { JSX, useState } from 'react'
import { NumberInput } from 'components/NumberInput'
import { chatWeightChoices, effectiveChatFontWeight } from 'lib/chatFonts'

const CUSTOM_WEIGHT = 'CUSTOM'

const WeightLabel = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <span className='mb-1.5 block text-xs font-medium uppercase tracking-wider text-dm-text-accent'>
    {children}
  </span>
)

export interface FontWeightFieldProps {
  label: string
  testIdPrefix: string
  fontKey: string
  valueWeight: number | null
  roleDefault: number
  onCommit: (weight: number) => void
  className: string
  fixedNote?: string
}

export const FontWeightField = ({
  label,
  testIdPrefix,
  fontKey,
  valueWeight,
  roleDefault,
  onCommit,
  className,
  fixedNote = ''
}: FontWeightFieldProps): JSX.Element => {
  const choices = chatWeightChoices(fontKey)
  const effective = effectiveChatFontWeight(fontKey, valueWeight ?? roleDefault, roleDefault)
  const [customFor, setCustomFor] = useState<string | null>(null)

  if (choices.kind === 'fixed') {
    const shipped = choices.presets[0] ?? roleDefault
    return (
      <div className='block'>
        <WeightLabel>{label}</WeightLabel>
        <select
          value={shipped}
          disabled
          aria-label={`${label} (fixed for this font)`}
          data-testid={`${testIdPrefix}-select`}
          className={`${className} disabled:cursor-not-allowed disabled:opacity-60`}
        >
          <option value={shipped}>{shipped}</option>
        </select>
        <p className='mt-1 text-[11px] text-dm-text-accent' data-testid={`${testIdPrefix}-hint`}>
          {`This font ships one weight (${shipped}).`}
          {fixedNote}
        </p>
      </div>
    )
  }

  if (choices.kind === 'cuts') {
    return (
      <div className='block'>
        <WeightLabel>{label}</WeightLabel>
        <select
          value={effective}
          onChange={(e) => onCommit(Number(e.target.value))}
          aria-label={label}
          data-testid={`${testIdPrefix}-select`}
          className={className}
        >
          {choices.presets.map((w) => (
            <option key={w} value={w}>{w}</option>
          ))}
        </select>
        <p className='mt-1 text-[11px] text-dm-text-accent' data-testid={`${testIdPrefix}-hint`}>
          {`This font ships these weights: ${choices.presets.join(', ')}.`}
        </p>
      </div>
    )
  }

  const axis = choices.custom ?? { min: effective, max: effective }
  const custom = customFor === fontKey || !choices.presets.includes(effective)
  return (
    <div className='block'>
      <WeightLabel>{label}</WeightLabel>
      <select
        value={custom ? CUSTOM_WEIGHT : String(effective)}
        onChange={(e) => {
          const next = e.target.value
          if (next === CUSTOM_WEIGHT) {
            setCustomFor(fontKey)
            return
          }
          setCustomFor(null)
          onCommit(Number(next))
        }}
        aria-label={label}
        data-testid={`${testIdPrefix}-select`}
        className={className}
      >
        {choices.presets.map((w) => (
          <option key={w} value={String(w)}>{w}</option>
        ))}
        <option value={CUSTOM_WEIGHT}>Custom</option>
      </select>
      {custom && (
        <NumberInput
          value={effective}
          min={axis.min}
          max={axis.max}
          onCommit={onCommit}
          aria-label={`${label} (custom, ${axis.min} to ${axis.max})`}
          data-testid={`${testIdPrefix}-custom`}
          className={`${className} mt-1.5`}
        />
      )}
      <p className='mt-1 text-[11px] text-dm-text-accent' data-testid={`${testIdPrefix}-hint`}>
        {fontKey === 'DEFAULT'
          ? `System font: pick a weight, or Custom for any whole number from ${axis.min} to ${axis.max}. Exact rendering depends on the viewer's device.`
          : `Variable font: pick a weight, or Custom for any whole number from ${axis.min} to ${axis.max}.`}
      </p>
    </div>
  )
}
